import axios from "axios";
import { API_BASE_URL, PRE_LOGIN_UUID, POST_LOGIN_UUID } from "../../constants/auth";
import { clearTokens, getAccessToken, saveTokens } from "../../utils/storage";
import { refreshToken } from "./authService";

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// ✅ REQUEST INTERCEPTOR
axiosInstance.interceptors.request.use(
  (config) => {
    const isPreLogin = config.headers.authType === "PreLogin";
    config.headers.Authorization = isPreLogin ? PRE_LOGIN_UUID : POST_LOGIN_UUID;

    if (!isPreLogin) {
      const token = getAccessToken();
      if (token) {
        config.headers.accessToken = token;
      }
    } else {
      delete config.headers.accessToken;
    }

    // Force PostLogin on /auth/verify
    if (config.url === "/auth/verify") {
      const token = getAccessToken();
      if (token) {
        config.headers.accessToken = token;
        config.headers.authType = "PostLogin";
        config.headers.Authorization = POST_LOGIN_UUID;
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ✅ RESPONSE INTERCEPTOR
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Debug log
    console.log("❗ Response Error Interceptor", {
      status: error.response?.status,
      url: originalRequest?.url,
      authType: originalRequest?.headers?.authType,
    });

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      originalRequest.headers?.authType !== "PreLogin"
    ) {
      originalRequest._retry = true;
      try {
        const response = await refreshToken();

        if (!response || !response.token || !response.refreshToken) {
          console.warn("🧯 Refresh returned null or incomplete tokens");
          clearTokens();
          return Promise.reject({ message: "Session expired" });
        }
        const { token: newAccessToken, refreshToken: newRefreshToken } = response;
        saveTokens({ accessToken: newAccessToken, refreshToken: newRefreshToken });

        originalRequest.headers.accessToken = newAccessToken;

        return axiosInstance(originalRequest); // ✅ Retry original request
      } catch (refreshError) {
        console.error("🔒 Refresh failed:", refreshError);
        clearTokens();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;